import React, { useState } from 'react';

// Define types for our component
interface CsvToYamlProps {}

// Helper function to guess the data type of a value
const convertValueType = (value: string): any => {
  // Empty values
  if (value === '') {
    return null;
  }
  
  // Boolean values
  if (value.toLowerCase() === 'true') {
    return true;
  }
  if (value.toLowerCase() === 'false') {
    return false;
  }
  
  // Null value
  if (value.toLowerCase() === 'null' || value.toLowerCase() === 'n/a') {
    return null;
  }
  
  // Number values
  if (/^-?\d+$/.test(value)) {
    return parseInt(value, 10);
  }
  if (/^-?\d+\.\d+$/.test(value)) {
    return parseFloat(value);
  }
  
  // Date values (ISO format)
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(.\d{3})?Z?)?$/.test(value)) {
    return value; // Keep as string for YAML but it's a valid date
  }
  
  // Default to string
  return value;
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// CSV to object array conversion function (intermediate step before YAML)
const csvToObjects = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    dynamicTyping: boolean;
    trimValues: boolean;
    skipEmptyLines: boolean;
  }
): Record<string, any>[] => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return [];
    }
    
    // Parse the headers if they exist
    let headers: string[] = [];
    let startRow = 0;
    
    if (options.header) {
      // Use the first row as headers
      const headerRow = lines[0];
      headers = headerRow
        .split(options.delimiter)
        .map(header => options.trimValues ? header.trim() : header);
      startRow = 1;
    } else {
      // Generate default column names
      const firstRow = lines[0].split(options.delimiter);
      headers = firstRow.map((_, index) => `column${index + 1}`);
    }
    
    // Process rows into objects
    const result: Record<string, any>[] = [];
    
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        const row: Record<string, any> = {};
        
        for (let j = 0; j < headers.length; j++) {
          const value = j < values.length ? values[j] : '';
          
          // Apply dynamic typing if enabled
          if (options.dynamicTyping) {
            row[headers[j]] = convertValueType(value);
          } else {
            row[headers[j]] = options.trimValues ? value.trim() : value;
          }
        }
        
        result.push(row);
      }
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};

// YAML conversion functions
const valueToYaml = (value: any, indent: number = 0): string => {
  const spaces = ' '.repeat(indent);
  
  if (value === null || value === undefined) {
    return 'null';
  }
  
  if (typeof value === 'string') {
    // Check if string needs to be quoted
    if (/[:#{}[\],&*!|>'"%@`]/.test(value) || value.trim() !== value || value === '') {
      return `"${value.replace(/"/g, '\\"')}"`;
    }
    return value;
  }
  
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    
    let result = '\n';
    for (const item of value) {
      result += `${spaces}- ${valueToYaml(item, indent + 2)}\n`;
    }
    return result.trim();
  }
  
  if (typeof value === 'object') {
    if (Object.keys(value).length === 0) {
      return '{}';
    }
    
    let result = '\n';
    for (const [key, val] of Object.entries(value)) {
      const formattedKey = /[:#{}[\],&*!|>'"%@`\s]/.test(key) ? `"${key}"` : key;
      result += `${spaces}${formattedKey}: ${valueToYaml(val, indent + 2)}\n`;
    }
    return result.trimEnd();
  }
  
  return String(value);
};

// Main function to convert object array to YAML
const objectsToYaml = (objects: Record<string, any>[]): string => {
  if (objects.length === 0) {
    return '[]';
  }
  
  let yaml = '';
  
  for (let i = 0; i < objects.length; i++) {
    const obj = objects[i];
    yaml += '- ';
    
    const entries = Object.entries(obj);
    if (entries.length === 0) {
      yaml += '{}\n';
      continue;
    }
    
    // Handle first entry differently (no newline before it)
    const [firstKey, firstVal] = entries[0];
    const formattedFirstKey = /[:#{}[\],&*!|>'"%@`\s]/.test(firstKey) ? `"${firstKey}"` : firstKey;
    
    if (typeof firstVal === 'object' && firstVal !== null && !Array.isArray(firstVal)) {
      yaml += `${formattedFirstKey}:${valueToYaml(firstVal, 2)}\n`;
    } else if (Array.isArray(firstVal)) {
      yaml += `${formattedFirstKey}:${valueToYaml(firstVal, 2)}\n`;
    } else {
      yaml += `${formattedFirstKey}: ${valueToYaml(firstVal)}\n`;
    }
    
    // Handle remaining entries
    for (let j = 1; j < entries.length; j++) {
      const [key, val] = entries[j];
      const formattedKey = /[:#{}[\],&*!|>'"%@`\s]/.test(key) ? `"${key}"` : key;
      const indent = '  ';
      
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        yaml += `${indent}${formattedKey}:${valueToYaml(val, 4)}\n`;
      } else if (Array.isArray(val)) {
        yaml += `${indent}${formattedKey}:${valueToYaml(val, 4)}\n`;
      } else {
        yaml += `${indent}${formattedKey}: ${valueToYaml(val)}\n`;
      }
    }
  }
  
  return yaml.trimEnd();
};

// Wrapper function to convert CSV to YAML
const csvToYaml = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    dynamicTyping: boolean;
    trimValues: boolean;
    skipEmptyLines: boolean;
  }
): string => {
  const objects = csvToObjects(csv, options);
  return objectsToYaml(objects);
};

// Main component to convert CSV to YAML
const CsvToYaml: React.FC<CsvToYamlProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the YAML output
  const [yamlOutput, setYamlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isYamlGenerated, setIsYamlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [dynamicTyping, setDynamicTyping] = useState<boolean>(true);
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [parseNestedObjects, setParseNestedObjects] = useState<boolean>(false);
  const [parseArrays, setParseArrays] = useState<boolean>(false);
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsYamlGenerated(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setCsvInput(event.target.result as string);
        setError('');
        setIsYamlGenerated(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Name,Age,Email,IsActive,JoinDate,Score
John Doe,32,john@example.com,true,2021-03-15,85.5
Jane Smith,28,jane@example.com,true,2020-11-20,92.0
Bob Johnson,45,bob@example.com,false,2019-07-08,76.8
Alice Brown,33,alice@example.com,true,2022-01-10,88.2
Charlie Wilson,,charlie@example.com,false,2020-02-28,`;

    setCsvInput(exampleCsv);
    setError('');
    setIsYamlGenerated(false);
    setYamlOutput('');
  };

  // Convert CSV to YAML
  const convertCsvToYaml = () => {
    setError('');
    setIsLoading(true);
    setIsYamlGenerated(false);
    setYamlOutput('');
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the objects from CSV
      const options = {
        header: hasHeader,
        delimiter,
        dynamicTyping,
        trimValues,
        skipEmptyLines
      };
      
      let objects = csvToObjects(csvInput, options);
      
      // Post-processing for nested objects and arrays if enabled
      if (parseNestedObjects || parseArrays) {
        objects = objects.map((item: Record<string, any>) => {
          const newItem: Record<string, any> = {};
          
          if (parseNestedObjects) {
            for (const [key, value] of Object.entries(item)) {
              if (typeof value === 'string' && key.includes('.')) {
                // Potential nested object notation (e.g., "address.city")
                const parts = key.split('.');
                if (parts.length > 1) {
                  let current = newItem;
                  for (let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if (!current[part]) {
                      current[part] = {};
                    }
                    current = current[part];
                  }
                  current[parts[parts.length - 1]] = value;
                } else {
                  newItem[key] = value;
                }
              } else {
                newItem[key] = value;
              }
            }
          } else {
            Object.assign(newItem, item);
          }
          
          if (parseArrays) {
            for (const [key, value] of Object.entries(newItem)) {
              if (typeof value === 'string' && value.includes(',')) {
                // Potential array values (comma-separated)
                newItem[key] = value.split(',').map(v => {
                  const trimmed = v.trim();
                  return dynamicTyping ? convertValueType(trimmed) : trimmed;
                });
              }
            }
          }
          
          return parseNestedObjects ? newItem : item;
        });
      }
      
      // Convert to YAML
      const yaml = objectsToYaml(objects);
      setYamlOutput(yaml);
      
      setIsYamlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to YAML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setYamlOutput('');
    setError('');
    setFileName('data');
    setIsYamlGenerated(false);
  };

  // Copy YAML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(yamlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download YAML file
  const downloadYaml = () => {
    const blob = new Blob([yamlOutput], { type: 'text/yaml;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'data'}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to YAML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to YAML format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Name,Age,Email
John Doe,30,john@example.com
Jane Smith,25,jane@example.com"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="dynamicTyping"
                    checked={dynamicTyping}
                    onChange={(e) => setDynamicTyping(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="dynamicTyping" className="ml-2 block text-sm text-gray-700">
                    Convert values to proper types (numbers, booleans, null)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="parseNestedObjects"
                    checked={parseNestedObjects}
                    onChange={(e) => setParseNestedObjects(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="parseNestedObjects" className="ml-2 block text-sm text-gray-700">
                    Parse nested objects (e.g., address.city)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="parseArrays"
                    checked={parseArrays}
                    onChange={(e) => setParseArrays(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="parseArrays" className="ml-2 block text-sm text-gray-700">
                    Parse comma-separated values as arrays
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToYaml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to YAML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              YAML Output
            </label>
            
            {isYamlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy YAML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadYaml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isYamlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    YAML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isYamlGenerated && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="yamlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={yamlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isYamlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to YAML!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated YAML for use in your application.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to YAML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool parses CSV (Comma-Separated Values) data and converts it into YAML (YAML Ain't Markup Language) format. CSV files are plain text files that store tabular data with each value separated by a delimiter (typically a comma). YAML is a human-friendly data serialization standard that is commonly used for configuration files and in applications where data is being stored or transmitted.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>Custom Headers</strong> - Use first row as headers or generate default column names</li>
              <li><strong>Data Type Detection</strong> - Automatically converts values to appropriate types</li>
              <li><strong>Value Trimming</strong> - Option to trim whitespace from field values</li>
              <li><strong>Empty Line Handling</strong> - Skip or include empty lines as needed</li>
              <li><strong>Nested Object Support</strong> - Parse dot notation keys into nested objects</li>
              <li><strong>Array Detection</strong> - Convert comma-separated field values into arrays</li>
              <li><strong>Quoted Value Support</strong> - Properly handles quoted fields in CSV</li>
              <li><strong>Pretty Output</strong> - Generate well-formatted, readable YAML</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better YAML property naming</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
              <li>Avoid commas in values unless properly quoted</li>
              <li>Use ISO date formats (YYYY-MM-DD) for better type conversion</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">YAML Structure Understanding</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>YAML represents data in a hierarchical structure with indentation to indicate nesting</li>
              <li>Each CSV row becomes a YAML list item (indicated by a leading dash)</li>
              <li>CSV headers become property names in the YAML objects</li>
              <li>Data types in YAML include strings, numbers, booleans, null, objects, and sequences</li>
              <li>YAML is widely used for configuration files, data exchange, and in projects using Docker, Kubernetes, Ansible, etc.</li>
              <li>YAML is more readable than JSON for complex configurations due to its use of indentation instead of brackets</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Conversion Limitations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>CSV has a flat structure while YAML can represent complex nested structures</li>
              <li>Type detection is based on value patterns and may not always be accurate</li>
              <li>Very large CSV files may cause browser performance issues</li>
              <li>Complex quoted fields with delimiters might require additional parsing logic</li>
              <li>This tool provides basic nested object and array parsing, but complex transformations may require custom code</li>
              <li>YAML's indentation-based syntax requires careful handling of multi-line strings</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Ensure your CSV data is well-formed with consistent columns</li>
              <li>Use headers to provide meaningful property names in the resulting YAML</li>
              <li>Choose the appropriate delimiter based on your data (comma is most common)</li>
              <li>Enable dynamic typing for automatic conversion of numbers, booleans, and null values</li>
              <li>Use the advanced options to customize the conversion process for your specific needs</li>
              <li>For very large files, consider processing in smaller chunks or using server-side conversion tools</li>
              <li>Review the generated YAML output for proper indentation and structure before using in applications</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Uses for YAML</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Configuration files for applications, tools, and frameworks</li>
              <li>Infrastructure as Code (IaC) with tools like Ansible, Kubernetes, and Docker Compose</li>
              <li>Continuous Integration/Continuous Deployment (CI/CD) pipelines</li>
              <li>Data exchange format in API responses</li>
              <li>Storing structured data where human readability is important</li>
              <li>Documentation and specification files</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToYaml;